/// <reference lib="dom" />
const requestQueue: { task: () => Promise<any>, resolve: (value: any) => void, reject: (reason?: any) => void }[] = [];
let activeRequests = 0;
const MAX_CONCURRENT_REQUESTS = 5;

function processQueue() {
    while (activeRequests < MAX_CONCURRENT_REQUESTS && requestQueue.length > 0) {
        const item = requestQueue.shift();
        if (item) {
            activeRequests++;
            item.task()
                .then(item.resolve)
                .catch(item.reject)
                .finally(() => {
                    activeRequests--;
                    processQueue();
                });
        }
    }
}

function enqueue<T>(task: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
        requestQueue.push({ task, resolve, reject });
        processQueue();
    });
}

export const api = {
    async fetch(url: string, options: RequestInit = {}): Promise<Response> {
        const mergedOptions: RequestInit = {
            ...options,
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
        };
        if (options.body && typeof options.body !== 'string') {
             mergedOptions.body = JSON.stringify(options.body);
        }
        const fetchTask = () => fetch(url, mergedOptions);
        const response = url.startsWith('/api/files/search') 
            ? await enqueue(fetchTask)
            : await fetchTask();
        if (response.status === 401) {
            if (!url.endsWith('/api/me')) {
                window.location.href = '/';
                return new Promise(() => {});
            }
        }
        if (!response.ok) {
            let errorMessage = `HTTP error! status: ${response.status}`;
            try {
                const errorData = await response.json();
                errorMessage = (errorData as { message: string }).message || errorMessage;
            } catch (e) { /* Ignore if response is not JSON */ }
            throw new Error(errorMessage);
        }
        return response;
    },
    async get<T>(url: string): Promise<T> {
        const response = await this.fetch(url);
        return response.json();
    },
    async _requestWithBody<T>(method: string, url: string, body?: any): Promise<T> {
        const response = await this.fetch(url, { method, body });
        if (response.status === 204) {
            return undefined as unknown as T;
        }
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
            return response.json();
        }
        return undefined as unknown as T;
    },
    async post<T>(url: string, body: any): Promise<T> {
        return this._requestWithBody('POST', url, body);
    },
    async put<T>(url: string, body: any): Promise<T> {
        return this._requestWithBody('PUT', url, body);
    },
    async patch<T>(url: string, body?: any): Promise<T> {
        return this._requestWithBody('PATCH', url, body);
    },
    async delete<T>(url: string): Promise<T> {
        return this._requestWithBody('DELETE', url);
    }
};